import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const dividerVariants = cva("", {
  variants: {
    orientation: {
      horizontal: "w-full",
      vertical: "h-full",
    },
    thickness: {
      thinner: "",
      thin: "",
      thick: "",
      thicker: "",
    },
    lineStyle: {
      solid: "border-solid",
      dashed: "border-dashed",
    },
    variant: {
      normal: "",
      subtle: "",
      muted: "",
    },
  },
  compoundVariants: [
    // Horizontal orientation with thickness
    {
      orientation: "horizontal",
      thickness: "thinner",
      class: "border-t-[0.5px]",
    },
    {
      orientation: "horizontal",
      thickness: "thin",
      class: "border-t-[1px]",
    },
    {
      orientation: "horizontal",
      thickness: "thick",
      class: "border-t-[2px]",
    },
    {
      orientation: "horizontal",
      thickness: "thicker",
      class: "border-t-[3px]",
    },
    // Vertical orientation with thickness
    {
      orientation: "vertical",
      thickness: "thinner",
      class: "border-l-[0.5px]",
    },
    {
      orientation: "vertical",
      thickness: "thin",
      class: "border-l-[1px]",
    },
    {
      orientation: "vertical",
      thickness: "thick",
      class: "border-l-[2px]",
    },
    {
      orientation: "vertical",
      thickness: "thicker",
      class: "border-l-[3px]",
    },
    // Normal variant colors
    {
      variant: "normal",
      class: "border-surface-outline-neutral-normal",
    },
    // Subtle variant colors
    {
      variant: "subtle",
      class: "border-surface-outline-neutral-subtle",
    },
    // Muted variant colors
    {
      variant: "muted",
      class: "border-surface-outline-neutral-muted",
    },
  ],
  defaultVariants: {
    orientation: "horizontal",
    thickness: "thin",
    lineStyle: "solid",
    variant: "normal",
  },
});

export interface DividerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof dividerVariants> {
  /**
   * The orientation of the divider
   * @default "horizontal"
   */
  orientation?: "horizontal" | "vertical";
  /**
   * The thickness of the divider
   * @default "thin"
   */
  thickness?: "thinner" | "thin" | "thick" | "thicker";
  /**
   * The style of the divider line
   * @default "solid"
   */
  lineStyle?: "solid" | "dashed";
  /**
   * The color variant of the divider
   * @default "normal"
   */
  variant?: "normal" | "subtle" | "muted";
  /**
   * Custom class name for the divider
   */
  className?: string;
}

const Divider = React.forwardRef<HTMLDivElement, DividerProps>(
  (
    {
      className,
      orientation = "horizontal",
      thickness = "thin",
      lineStyle = "solid",
      variant = "normal",
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        role="separator"
        aria-orientation={orientation}
        className={cn(
          dividerVariants({ orientation, thickness, lineStyle, variant }),
          className
        )}
        {...props}
      />
    );
  }
);

Divider.displayName = "Divider";

export { Divider, dividerVariants };
